import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@comps/ui/tooltip";
import { CompozzError } from "@dto/CompozzError";
import { ObjectInfos } from "@dto/objects";
import { Filter } from "@dto/records";
import { View } from "@dto/views";
import { validateFilters } from "@utils/filters";
import { toastError } from "@utils/toasts";
import { TW_FilterEditor } from "@utils/twClasses";
import { useTranslation } from "react-i18next";
import { translate } from "src/i18n/i18n";
import { useModelStore } from "src/store";
import FilterItem from "./FilterItem";


interface FilterEditorProps {
    filters: Filter[]
    relationFilters?: Filter[]
    onClose: () => void
    disabled: boolean
    allowNewFilters?: boolean
}

function FilterEditor({ filters, relationFilters = [], onClose, disabled, allowNewFilters = true }: FilterEditorProps) {

    const updateSelectedView = useModelStore((state) => state.updateSelectedView);
    const selectedObjectInfos = useModelStore((state) => state.selectedObjectInfos);
    const sharedObjectInfos = useModelStore((state) => state.sharedObjectInfos);
    const selectedView = useModelStore((state) => state.selectedView);
    const { t } = useTranslation();

    const fields = selectedObjectInfos?.fields;
    if (!fields) {
        return null;
    }

    let filterByPathKeys: Map<string, Filter> = new Map();
    const filterPathKeys: string[] = [];
    filters.forEach((filter) => {
        if (filter.groupFilter &&
            filter.groupFilter.filters &&
            filter.groupFilter.filters.length > 0 &&
            filter.groupFilter.filters[0].singleFilter?.pathKey) {
            const key = filter.groupFilter.filters[0].singleFilter.pathKey;
            filterByPathKeys.set(key, filter);
            filterPathKeys.push(key);
        }
    })

    const onViewChange = (objectInfos: ObjectInfos, view: View) => {
        updateSelectedView(objectInfos, view, null, sharedObjectInfos);
        // No need to call fetchView as it is triggered by the view change
        // fetchView(view);
    }

    const onRemoveFilter = (pathKey: string | null) => {
        if (!pathKey) {
            return;
        }

        const newFilters = (selectedView?.filter?.groupFilter?.filters || []).filter(f => {
            const firstPathKey = f.groupFilter?.filters[0]?.singleFilter?.pathKey.split("-")[0];
            var isDifferent = f.groupFilter?.filters?.length && f.groupFilter?.filters[0]?.singleFilter?.pathKey !== pathKey;
            if (isDifferent) {
                isDifferent = !firstPathKey?.includes(pathKey);
            }
            return isDifferent;

        });
        validateAllFilters(newFilters);
    }

    const onFilterChange = (filter: Filter | null) => {
        if (!filter || !filter.groupFilter?.filters?.length || !filter.groupFilter?.filters[0]?.singleFilter?.pathKey) {
            return;
        }

        const pathKey = filter.groupFilter?.filters[0]?.singleFilter?.pathKey;
        const newFilters = (selectedView?.filter?.groupFilter?.filters || []).map(f => {
            if (f.groupFilter?.filters?.length && f.groupFilter?.filters[0]?.singleFilter?.pathKey === pathKey) {
                return filter;
            }
            return f;
        });
        const newFilter = newFilters.find(f => f.groupFilter?.filters?.length && f.groupFilter?.filters[0]?.singleFilter?.pathKey === pathKey);
        if (!newFilter) {
            newFilters.push(filter);
        }

        validateAllFilters(newFilters);
    }

    function validateAllFilters(filters: Filter[] | null) {

        validateFilters(filters, (newFilter) => {
            const newView = {
                ...selectedView!,
                filter: newFilter
            }
            onViewChange(selectedObjectInfos!, newView);
        }, (error) => {
            const cError = error as CompozzError;
            if (cError.i18n) {
                toastError(translate(t, cError.i18n, cError.params));
            } else {
                toastError(cError.message);
            }
            onClose();
        });
    }


    // Collect relation filter path keys to exclude them from used path keys for regular filters
    const relationFilterPathKeys: string[] = [];
    relationFilters.forEach((filter) => {
        if (filter.groupFilter?.filters?.[0]?.singleFilter?.pathKey) {
            relationFilterPathKeys.push(filter.groupFilter.filters[0].singleFilter.pathKey);
        }
    });

    const sortedFilterByPathKeys = Array.from(filterByPathKeys.keys()).sort();
    const allUsedPathKeys = [...sortedFilterByPathKeys, ...relationFilterPathKeys];
    const itemKeyPref = selectedView?._key + "-filter-"
    const relationItemKeyPref = "relation-filter-"

    // Render relation filters (always disabled)
    const renderRelationFilters = () => (
        relationFilters.length > 0 && relationFilters.map((f, index) => (
            <FilterItem 
                key={relationItemKeyPref + index} 
                filter={f} 
                objectInfos={selectedObjectInfos!} 
                onFilterChange={() => {}} 
                onFilterRemove={() => {}} 
                usedFilterPathKeys={allUsedPathKeys} 
                disabled={true} 
            />
        ))
    );

    if (disabled) {
        return (
            <TooltipProvider>
                <Tooltip delayDuration={100}>
                    <TooltipTrigger>
                        <div className={TW_FilterEditor}>
                            {renderRelationFilters()}
                            {filterPathKeys.length > 0 &&
                                filterPathKeys.map((key, index) => {
                                    const f = filterByPathKeys.get(key)!;
                                    return <FilterItem key={itemKeyPref + index} filter={f} objectInfos={selectedObjectInfos!} onFilterChange={onFilterChange} onFilterRemove={onRemoveFilter} usedFilterPathKeys={allUsedPathKeys} disabled={disabled} />
                                })}
                        </div>
                    </TooltipTrigger>
                    <TooltipContent>
                        {t("filter.edition.forbidden.tooltip")}
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        )
    } else {
        return (
            <div className={TW_FilterEditor}>
                {renderRelationFilters()}
                {filterPathKeys.length > 0 &&
                    filterPathKeys.map((key, index) => {
                        const f = filterByPathKeys.get(key)!;
                        return <FilterItem key={itemKeyPref + index} filter={f} objectInfos={selectedObjectInfos!} onFilterChange={onFilterChange} onFilterRemove={onRemoveFilter} usedFilterPathKeys={allUsedPathKeys} disabled={disabled} />
                    })}
                {allowNewFilters && <FilterItem key={itemKeyPref + filterPathKeys.length} filter={null} objectInfos={selectedObjectInfos!} onFilterChange={onFilterChange} onFilterRemove={onRemoveFilter} usedFilterPathKeys={allUsedPathKeys} disabled={disabled} />}
            </div>
        )
    }
}

export default FilterEditor;
